import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar.tsx";
import { Footer } from "@/components/layout/Footer.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Copy, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast.ts";
import {
  clonePublishedStory,
  getPublishedStoryBySlug,
  type PublishedStory,
} from "@/lib/storage/stories.ts";

function SkillUsesList({ skillUses }: { skillUses: string[] }) {
  const filtered = skillUses.filter((s) => s.trim());
  if (filtered.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {filtered.map((use, idx) => (
        <Badge key={`su-${idx}`} variant="outline" className="text-[10px] font-normal">
          {use}
        </Badge>
      ))}
    </div>
  );
}

export function PublicStory() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [story, setStory] = useState<PublishedStory | null>(null);
  const [loading, setLoading] = useState(true);
  const [cloning, setCloning] = useState(false);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    const load = async () => {
      const published = await getPublishedStoryBySlug(slug);
      setStory(published);
      setLoading(false);
    };

    void load();
  }, [slug]);

  const handleClone = async () => {
    if (!story) return;
    setCloning(true);
    try {
      const cloned = await clonePublishedStory(story.snapshot);
      toast({
        title: t("catalog.clonedTitle"),
        description: t("catalog.clonedDescription"),
      });
      navigate(`/story/${cloned.id}`);
    } catch (error) {
      toast({
        title: t("catalog.cloneFailedTitle"),
        description: error instanceof Error ? error.message : t("publish.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setCloning(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 mx-auto w-full max-w-4xl px-6 py-10 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <Button asChild variant="outline">
            <Link to="/catalog">{t("catalog.backToCatalogue")}</Link>
          </Button>
          {story && (
            <Button onClick={handleClone} disabled={cloning} className="gap-2">
              {cloning
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Copy className="h-4 w-4" />}
              {cloning ? t("catalog.cloning") : t("catalog.cloneAsDraft")}
            </Button>
          )}
        </div>

        {loading
          ? <p className="text-muted-foreground">{t("common.loading")}</p>
          : !story
          ? <p className="text-muted-foreground">{t("catalog.storyNotFound")}</p>
          : (
            <Card className="border-2">
              <CardHeader className="space-y-2">
                <CardTitle className="font-pixel text-sm uppercase">{story.title}</CardTitle>
                <p className="text-xs text-muted-foreground">
                  {t("catalog.publishedOn", {
                    date: new Date(story.publishedAt).toLocaleString(),
                  })}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h2 className="font-semibold">{t("catalog.intro")}</h2>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {story.snapshot.storySeed.storyIntro || t("catalog.noIntro")}
                  </p>
                </div>

                <div>
                  <h2 className="font-semibold">{t("catalog.mainObjective")}</h2>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {story.snapshot.storySeed.mainObjective.description ||
                      t("catalog.noObjective")}
                  </p>
                  <SkillUsesList
                    skillUses={story.snapshot.storySeed.mainObjective.skillUses}
                  />
                </div>

                <div>
                  <h2 className="font-semibold">{t("catalog.storyTwist")}</h2>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {story.snapshot.storySeed.storyTwist || t("catalog.noTwist")}
                  </p>
                </div>

                <div>
                  <h2 className="font-semibold">{t("catalog.skills")}</h2>
                  <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                    {story.snapshot.storySeed.skills.filter((s) => s.name.trim()).length === 0
                      ? <li>{t("catalog.noSkills")}</li>
                      : story.snapshot.storySeed.skills
                        .filter((skill) => skill.name.trim())
                        .map((skill, idx) => (
                          <li key={`skill-${idx}`}>
                            {skill.emoji ? `${skill.emoji} ` : ""}
                            {skill.name}
                            {skill.application
                              ? (
                                <span className="text-xs text-foreground/60 ml-1">
                                  — {skill.application}
                                </span>
                              )
                              : null}
                          </li>
                        ))}
                  </ul>
                </div>

                <div>
                  <h2 className="font-semibold">{t("story.characters")}</h2>
                  <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                    {story.snapshot.storySeed.characters.filter((c) => c.name.trim()).length === 0
                      ? <li>{t("catalog.noCharacters")}</li>
                      : story.snapshot.storySeed.characters
                        .filter((character) => character.name.trim())
                        .map((character, idx) => (
                          <li key={`character-${idx}`}>
                            <div>
                              {character.emoji ? `${character.emoji} ` : ""}
                              {character.name}
                              {character.role ? ` — ${character.role}` : ""}
                            </div>
                            <SkillUsesList skillUses={character.skillUses} />
                          </li>
                        ))}
                  </ul>
                </div>

                <div>
                  <h2 className="font-semibold">{t("story.locations")}</h2>
                  <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                    {story.snapshot.storySeed.locations.filter((l) => l.name.trim()).length === 0
                      ? <li>{t("catalog.noLocations")}</li>
                      : story.snapshot.storySeed.locations
                        .filter((location) => location.name.trim())
                        .map((location, idx) => (
                          <li key={`location-${idx}`}>
                            <div>
                              {location.emoji ? `${location.emoji} ` : ""}
                              {location.name}
                              {location.description ? ` — ${location.description}` : ""}
                            </div>
                            <SkillUsesList skillUses={location.skillUses} />
                          </li>
                        ))}
                  </ul>
                </div>

                <div>
                  <h2 className="font-semibold">{t("story.sideQuests")}</h2>
                  <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                    {story.snapshot.storySeed.sideQuests.filter((q) => q.description.trim())
                        .length ===
                        0
                      ? <li>{t("catalog.noSideQuests")}</li>
                      : story.snapshot.storySeed.sideQuests
                        .filter((quest) => quest.description.trim())
                        .map((quest, idx) => (
                          <li key={`quest-${idx}`}>
                            <div>{quest.description}</div>
                            <SkillUsesList skillUses={quest.skillUses} />
                          </li>
                        ))}
                  </ul>
                </div>

                <div>
                  <h2 className="font-semibold">{t("playground.encounters")}</h2>
                  <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                    {story.snapshot.storySeed.encounters.filter((e) => e.description.trim())
                        .length ===
                        0
                      ? <li>{t("catalog.noEncounters")}</li>
                      : story.snapshot.storySeed.encounters
                        .filter((encounter) => encounter.description.trim())
                        .map((encounter, idx) => (
                          <li key={`encounter-${idx}`}>
                            <div>
                              {encounter.description}
                              {encounter.type
                                ? (
                                  <Badge variant="secondary" className="ml-2 text-[10px]">
                                    {encounter.type}
                                  </Badge>
                                )
                                : null}
                            </div>
                            <SkillUsesList skillUses={encounter.skillUses} />
                          </li>
                        ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}
      </main>
      <Footer />
    </div>
  );
}
